import { SearchableSelect } from '@/shared/components/ui/SearchableSelect'
import { Modal } from '@/shared/components/ui/Modal'
import type { AssetHolding } from '@/modules/inventory/holdings'
import type { AssetHoldingLocation } from '@/modules/inventory/holdings'

export interface BatchLocationTransferFormData {
  location_id: string
  quantity: string
  occurred_at: string
  reason: string
}

interface BatchLocationTransferModalProps {
  isOpen: boolean
  batch: AssetHolding | null
  locations: AssetHoldingLocation[]
  formData: BatchLocationTransferFormData
  setFormData: (data: BatchLocationTransferFormData) => void
  formErrors: Record<string, string>
  submitting: boolean
  error?: string | null
  title: string
  submitLabel: string
  onClose: () => void
  onSubmit: () => void
  onCreateLocation: () => void
  tc: (key: string) => string
}

export function BatchLocationTransferModal({
  isOpen,
  batch,
  locations,
  formData,
  setFormData,
  formErrors,
  submitting,
  error,
  title,
  submitLabel,
  onClose,
  onSubmit,
  onCreateLocation,
  tc,
}: BatchLocationTransferModalProps) {
  const availableQuantity = Number(batch?.quantity ?? 0)
  const quantity = Number(formData.quantity || 0)
  const isPartial = quantity > 0 && quantity < availableQuantity

  const locationOptions = [
    { value: '', label: tc('fields.locationNone') },
    ...locations.map((location) => ({ value: location.id, label: location.name })),
  ]

  const inputCls = (field: string) =>
    `w-full px-4 py-2 rounded-lg border ${
      formErrors[field] ? 'border-red-300' : 'border-slate-300'
    } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-amber-500`

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      onSubmit={onSubmit}
      submitLabel={submitLabel}
      submitting={submitting}
      size="md"
    >
      <div className="space-y-4">
        {error && (
          <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        {batch && (
          <div className="rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 p-4 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-500 dark:text-slate-400">{tc('fields.batch')}</span>
              <span className="text-sm font-semibold text-slate-900 dark:text-white text-right">
                {batch.name ?? batch.asset?.name ?? batch.id}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-500 dark:text-slate-400">{tc('fields.currentLocation')}</span>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200 text-right">
                {batch.location?.name ?? tc('fields.locationNone')}
              </span>
            </div>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm text-slate-500 dark:text-slate-400">{tc('fields.availableQuantity')}</span>
              <span className="text-sm font-medium text-slate-800 dark:text-slate-200">
                {availableQuantity.toLocaleString(undefined, { maximumFractionDigits: 3 })} {batch.uom}
              </span>
            </div>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {tc('fields.targetLocation')}
          </label>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <SearchableSelect
                options={locationOptions}
                value={formData.location_id}
                onChange={(value) => setFormData({ ...formData, location_id: value })}
                placeholder={tc('fields.locationPlaceholder')}
                disabled={submitting}
                error={!!formErrors.location_id}
              />
            </div>
            <button
              type="button"
              onClick={onCreateLocation}
              disabled={submitting}
              title={tc('actions.newLocation')}
              className="shrink-0 flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-50 hover:border-emerald-400 hover:text-emerald-600 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          {formErrors.location_id && <p className="mt-1 text-sm text-red-600">{formErrors.location_id}</p>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('fields.quantity')}
            </label>
            <input
              type="number"
              min="0"
              step="0.001"
              value={formData.quantity}
              onChange={(event) => setFormData({ ...formData, quantity: event.target.value })}
              className={inputCls('quantity')}
              disabled={submitting}
            />
            {formErrors.quantity && <p className="mt-1 text-sm text-red-600">{formErrors.quantity}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {tc('fields.occurredAt')}
            </label>
            <input
              type="date"
              value={formData.occurred_at}
              onChange={(event) => setFormData({ ...formData, occurred_at: event.target.value })}
              className={inputCls('occurred_at')}
              disabled={submitting}
            />
          </div>
        </div>

        {isPartial && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
            {tc('messages.partialInfo')}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            {tc('fields.reason')}
          </label>
          <textarea
            value={formData.reason}
            onChange={(event) => setFormData({ ...formData, reason: event.target.value })}
            className={`${inputCls('reason')} min-h-20 resize-y`}
            disabled={submitting}
            placeholder={tc('fields.reasonPlaceholder')}
          />
        </div>
      </div>
    </Modal>
  )
}
